import { useLoading } from '@contexts/LoadingContext'
import { useError } from '@contexts/ErrorContext'
import { createSubchainsAPI, deleteSubchainsAPI, getSCTemplateAPI, getSubchainsAPI, updateSubchainsAPI } from '@services/subchainsAPI';
import { useCallback, useEffect, useState } from 'react';
import { scInitialData as initialData } from '../../helperFunctions/initalTableData';
import { getAGMsAPI } from '@services/agmsAPI';
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext'
import { scValidateUsAndDs, validateTableData } from '../../helperFunctions/validateTableData';
import { SubchainSchema } from '../../tableConfig/jobControlSchemas';
import { /*areObjectsEqual,*/ deepEqual } from '@utils/areObjectsEqual';
import { useSuccess } from '@contexts/SuccessContext'
import { recalculateElongValues } from '../helpers/elongUpdater';
import { getUSDistance, getDSDistance } from '../../helperFunctions/chainDistanceCalcs';
import { useUser } from '@contexts/UserContext';

export const useSubchainActions = (selectedJob, requestConfirm) => {
  const { showLoading, hideLoading, showSpinner, hideSpinner } = useLoading();
  const { showError } = useError();

  // const [data, setData] = useState(initialData);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]); //use to compare original and changed data when time to submit
  const [agmOptions, setAgmOptions] = useState([]); //added by Bryan for fetched agm options
  const [agmRawData, setAgmRawData] = useState([]); //added by Bryan for fetched agm raw data - sc validation
  const [isUnlockedWithPOIs, setIsUnlockedWithPOIs] = useState(false)

  const [validationErrors, setValidationErrors] = useState([]);
  const { setHasUnsavedChanges } = useUnsavedChanges();
  const { timeoutSuccess } = useSuccess();
  const { setStoredJob } = useUser()

  //fetch subchains when component loads or if job selected/changed
  useEffect(() => {
    if (!selectedJob) return;

    fetchSubchains(selectedJob.value);
    fetchAGMs(selectedJob.value); //fetch agms for dropdown select
    setValidationErrors([]);
    setIsUnlockedWithPOIs(selectedJob.POICount > 0 && !selectedJob.IsLocked)

  }, [selectedJob]); //eslint-disable-line

  //fetch subchains
  const fetchSubchains = async (jobId) => {
    setData([])
    showLoading("Getting subchains...")

    let response = await getSubchainsAPI(jobId);

    if (!response.success) {
      hideLoading()
      showError(`Error getting subchains: ${response.message}`)
    } else {

      if (response.data.length > 0) {

        //remove CreatedAt since that will never be updated in the frontend
        //formatting boolean fields to match dropdown options
        let formattedData = response.data.map(({ CreatedAt, ...subchain }, index) => {
          // let orderNum;
          // if(!subchain.OrderNum) {
          //   orderNum = index + 1;
          //   // setHasUnsavedChanges(true);
          // }
          return {
            ...subchain,
            // OrderNum: orderNum ?? index + 1 //adds an ordernum if nothing is there.
          }
        });

        setOriginalData([...formattedData]);

        formattedData = response.data.map(({ ...subchain }, index) => {
          // let orderNum;
          // if(!subchain.OrderNum) {
          //   orderNum = index + 1;
          //   // setHasUnsavedChanges(true);
          // }
          return {
            ...subchain,
            OrderNum: subchain.OrderNum != null ? subchain.OrderNum : index + 1 //adds an ordernum if nothing is there.
          }
        });

        formattedData.sort((a, b) => a.OrderNum - b.OrderNum);

        setData(formattedData);
        // setOriginalData(formattedData)
        // hideLoading()
        return
      }

      //if no data was returned from the fetch, reset state to initial data
      setData([]);
      // hideLoading()
    }
  }

  //fetch agms for dropdown selects in editable table
  //will also need for sc data validation
  //added by Bryan
  const fetchAGMs = async (jobId) => {
    setAgmRawData([])
    showLoading("Getting AGMs...")

    let response = await getAGMsAPI(jobId);

    if (!response.success) {
      hideLoading()
      showError(`Error getting AGMs: ${response.message}`)
    } else {

      if (response.data.length > 0) {

        //verify we have real chain values for all agms
        //if we don't, sort agms by Est_Chain
        const agmsHaveRealChain = response.data.every(
          agm => agm.Real_Chain !== null && agm.Real_Chain !== undefined
        );

        const sortBy = agmsHaveRealChain ? "Real_Chain" : "Est_Chain";
        
        const agms = response.data.sort((a,b) => a[sortBy] - b[sortBy])

        //formatting response data to fit option for dropdown selects
        const formattedData = agms.map((agm) => {
          return {
            value: agm.AGMID,
            label: agm.Site_Name
          }
        })

        setAgmOptions(formattedData);
        setAgmRawData(agms);

        hideLoading()
        return
      }

      hideLoading()
    }

  }

  const handleSubmit = async (setMissingColumns, setFileName) => {
    //show loading message, remove old submit message if exists
    showLoading("Submitting...");

    //validate
    const isValid = await validateTableData(data, setValidationErrors, SubchainSchema, agmOptions)

    if (!isValid) {
      hideLoading();
      showError('There are validation errors - There is a description of errors below the table and highlighted in red on the table.')
      return;
    }

    //submit subchains to api
    //api expects arrays of subchains, so create empty arrays to push new/updated subchains into
    let newSubchains = []
    let updatedSubchains = []

    const originalDataMap = new Map(originalData.map(row => [row.SCID, row]));

    for (const subchain of data) {

      //UPDATE subchains - subchains that are not new (have a SCID) and are different from the original data (to reduce api calls)
      if (subchain.hasOwnProperty("SCID")) {

        //compare to original data to see if subchain data has changed
        const originalRow = originalDataMap.get(subchain.SCID);

        // if (!areObjectsEqual(subchain, originalRow)) {
        //   updatedSubchains.push(subchain)
        // }

        if (!deepEqual(subchain, originalRow)) {
          updatedSubchains.push(subchain)
        }

      } else {

        //CREATE subchains - if no SCID, it's a new subchain
        const newSubchain = {
          ...subchain,
          JobID: selectedJob.value,
        }

        newSubchains.push(newSubchain)

      }

    }

    //if no changes made, no need to update state, also don't want to tell user it saved when nothing happened
    if (newSubchains.length === 0 && updatedSubchains.length === 0) {

      setHasUnsavedChanges(false)

      hideLoading()

      timeoutSuccess("No changes")

      return
    }

    //submit updated subchains are if there are any
    if (updatedSubchains.length > 0) {
      const response = await updateSubchainsAPI(updatedSubchains)

      if (!response.success) {
        hideLoading()

        showError(`Error updating subchains: ${response.message}`)

        return
      }
    }

    //get all the subchains that aren't new, for updating state later
    let dataToUpdate = data.filter(subchain => subchain.hasOwnProperty("SCID"))

    //submit new subchains are if there are any
    if (newSubchains.length > 0) {
      const response = await createSubchainsAPI(newSubchains)

      if (!response.success) {
        hideLoading()

        showError(`Error creating subchains: ${response.message}`)

        return
      }

      //format new subchains, now with SCIDs
      const subchainsWithIDs = response.data.map(({ CreatedAt, ...subchain }) => {
        return {
          ...subchain,
        }
      })

      dataToUpdate.push(...subchainsWithIDs)
    }

    dataToUpdate.sort((a,b) => a.OrderNum - b.OrderNum)
    
    //update state
    setData(dataToUpdate)
    setOriginalData(structuredClone(dataToUpdate))
    setValidationErrors([]) //here instead of inside validation function because that causes table to re-render (holds up submit function for tables with a lot of subchains)

    setStoredJob(prev => {
      return {
        ...prev,
        SubchainCount: dataToUpdate.length
      }
    })
    //if excel import had missing columns, reset message
    setMissingColumns([])

    //remove excel file name from page
    setFileName(null)

    hideLoading()

    setHasUnsavedChanges(false)

    timeoutSuccess("Saved");
    //recalculate Elongation Percents of agms
    let evalResult = recalculateElongValues(dataToUpdate, agmRawData);
    if (evalResult instanceof Error) {
      showError(`Error recalculating Elongation Percents: ${evalResult.message}`);
    }
  }

  const handleDownloadSCTemplate = async () => {
    //download logic
    showLoading('Getting Template');

    let response = await getSCTemplateAPI();

    if (response && response.success) {
      hideLoading();
      const link = document.createElement('a');
      link.href = response.url;
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      hideLoading();
      showError(response.message);
    }
  }

  const resetAfterDelete = (setMissingColumns, setFileName) => {
    //reset table state 
    setData(initialData);

    //if excel import had missing columns, reset message
    setMissingColumns([])

    //remove excel file name from page
    setFileName(null)
  }
  
  const handleDeleteAllSubchains = async (setMissingColumns, setFileName) => {
    //confirm user wants to delete all subchains
    const isConfirmed = await requestConfirm('Are you sure you want to delete all subchains?', "Delete Subchains", "Cancel");
    if (!isConfirmed) return

    // user confirmed
    showSpinner("Deleting subchains...")

    //if subchain has id, it exists in database, so need to delete it from there
    const deleteSubchainsFromDB = data
      .filter(subchain => subchain.SCID !== undefined)
      .map(subchain => subchain.SCID);

    const JobID = selectedJob.value;

    if (deleteSubchainsFromDB.length > 0) {

      const response = await deleteSubchainsAPI(deleteSubchainsFromDB, JobID);

      if (response && response.success) {
        resetAfterDelete(setMissingColumns, setFileName)

        hideSpinner()

        setHasUnsavedChanges(false)

        timeoutSuccess("Delete successful");
        //recalculate Elongation Percents of agms
        let evalResult = recalculateElongValues([], agmRawData);
        if (evalResult instanceof Error) {
          showError(`Error recalculating Elongation Percents: ${evalResult.message}`);
        }

        setStoredJob(prev => {
          return {
            ...prev,
            SubchainCount: 0
          }
        })

      } else {

        showError(`Error deleting subchains: ${response.message}`)
        hideSpinner()

        return
      }
    } else {
      resetAfterDelete(setMissingColumns, setFileName)
    }

    hideSpinner()
  }

  const recalculateSegment = useCallback((newData, row, affectedIndex) => {
    // 1. update OrderNum, starting from index where addition/deletion happened (needs updating from this point downstream)
    for (let i = affectedIndex; i < newData.length; i++) {
        newData[i] = { ...newData[i], OrderNum: i + 1 }
    }

    // 2. find segment where subchain added/deleted
    const { US_Site, DS_Site } = row

    let start = affectedIndex
    let end = affectedIndex

    while (start > 0 && newData[start - 1].US_Site === US_Site && newData[start - 1].DS_Site === DS_Site) start--
    while (end < newData.length - 1 && newData[end + 1].US_Site === US_Site && newData[end + 1].DS_Site === DS_Site) end++

    // 3. update ds/us distance for only affected segment
    for (let i = start; i <= end; i++) {
      
        //add 'true' so cache can be updated with added/deleted row
        const Dist_To_DS = getDSDistance(agmRawData, newData, newData[i], true)
        const Dist_To_US = getUSDistance(agmRawData, newData, newData[i], true)

        newData[i] = {
            ...newData[i],
             Dist_To_US,
            Dist_To_DS
        }
    }
  }, [agmRawData])

  const handleDeleteOneSubchain = useCallback(async (rowData, rowIndex) => {
    showLoading("Deleting subchain...")

    const subchainID = rowData.SCID
    const JobID = selectedJob.value;

    //if subchain has id, it exists in database, so need to delete it from there
    if (subchainID) {

      const response = await deleteSubchainsAPI([subchainID], JobID);

      if (!response.success) {
        hideLoading()

        showError(`Error deleting subchain: ${response.message}`)

        return
      }
    }

    //remove subchain from state, update order nums, recalculate us/ds distances

    setData(prevData => {

        let newData = []

        //if there was only one item in state before, now there will be zero, so replace with initial data
        if(prevData.length <= 1){
                
            newData = [...initialData]

        } else {
                
            newData = [...prevData];
    
            newData.splice(rowIndex, 1);

            recalculateSegment(newData, rowData, rowIndex)

        }

        return newData;
    });

    hideLoading()

    timeoutSuccess("Delete successful");

    //recalculate Elongation Percents of agms - filter out subchain that was deleted
    let scArray = data.filter((subchain) => subchain.SCID !== subchainID);
    let evalResults = recalculateElongValues(scArray, agmRawData);
    if (evalResults instanceof Error) {
      showError(`Error recalculating Elongation Percents: ${evalResults.message}`);
    }

    setStoredJob(prev => {
      return {
        ...prev,
        SubchainCount: scArray.length
      }
    })

  }, [showLoading, deleteSubchainsAPI, hideLoading, initialData, setData, agmRawData]); //eslint-disable-line

  const addSubchainRow = useCallback(
    // use async/await so we can show the loading spinner during this action, since calculations may be heavy on large tables
    async (schema, setData, insertIndex, newRowData) => {
      
      showSpinner("Adding subchain...");
      
      // async so we can pause execution briefly
      // waiting for requestAnimationFrame yields control back to the browser,
      // allowing React to paint the loading spinner before blocking the main
      // thread with synchronous calculations
      await Promise.resolve(requestAnimationFrame);
      
      try {
        //add the row
        let newRow = {};
        
        let closestSegment = scValidateUsAndDs(newRowData.lat, newRowData.lng, agmRawData)
        
        const US_Site = closestSegment.closestSegment.upstream.AGMID;
        const DS_Site = closestSegment.closestSegment.downstream.AGMID;
        
        Object.keys(schema).forEach(key => {
          newRow[key] = "";
        });

        //replace empty fields with the data that the user entered
        if (newRowData) {
          
          newRow = {
            ...newRow,
            ...newRowData,
            lat: Number(newRowData.lat),
            lng: Number(newRowData.lng),

            US_Site,
            DS_Site,
          };
        }

        setData(prevData => {
          const newData = [...prevData];

          newData.splice(insertIndex, 0, newRow);

          recalculateSegment(newData, newRow, insertIndex);

          return newData;

        });

      } finally {

        hideSpinner();

      }
    },
    [agmRawData, recalculateSegment, showSpinner, hideSpinner]
  );


  return {
    handleSubmit,
    handleDownloadSCTemplate,
    handleDeleteAllSubchains,
    handleDeleteOneSubchain,
    agmOptions,
    agmRawData,
    data,
    setData,
    validationErrors,
    addSubchainRow,
    isUnlockedWithPOIs
  }
}